import { useState } from 'react';

import { appBorders, appColors, BackrsIcon, Tier, TIERS } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { shouldSchoolLevelBeFiltered } from '@backrs_ui/grow-ui/src/components/MarkersOfSuccess/markersOfSuccess.util';
import { SCHOOL_LEVEL, SchoolLevel } from '@backrs_ui/grow-ui/src/models/models';

import { Box, Popover, Typography, SxProps, Theme, PopoverProps } from '@mui/material';

const MARKERS_OF_SUCCESS_STRINGS = appStrings.modules.markersOfSuccess;

type MiddleButtonProps = Readonly<{
  setIsMiddleButtonPopoverOpen: (value: boolean) => void;
  isMiddleButtonPopoverOpen: boolean;
  setTabTwoTier: (value: Tier) => void;
  setTabTwoSchoolLevel: (value: SchoolLevel) => void;
  tabTwoTier: Tier | undefined;
  tabTwoSchoolLevel: SchoolLevel | undefined;
}>;

export const MiddleButton = ({
  setIsMiddleButtonPopoverOpen,
  isMiddleButtonPopoverOpen,
  setTabTwoTier,
  setTabTwoSchoolLevel,
  tabTwoTier,
  tabTwoSchoolLevel,
}: MiddleButtonProps) => {
  const [hoveredCompareWithElement, setHoveredCompareWithElement] = useState<
    'education' | 'tracks' | undefined
  >();

  const [secondaryPopoverContent, setSecondaryPopoverContent] = useState<
    'education' | 'tracks' | undefined
  >();

  /** props used for both popovers */
  const commonPopoverProps: Omit<PopoverProps, 'open' | 'anchorEl' | 'onClose'> = {
    anchorOrigin: { vertical: 'center', horizontal: 'right' },
    transformOrigin: { vertical: 'center', horizontal: 'left' },
    PaperProps: {
      sx: paperSx,
    },
  };

  return (
    <Box sx={middleButtonStyle}>
      <Box
        id="popover-anchor"
        onClick={() => setIsMiddleButtonPopoverOpen(true)}
        sx={doubleArrowIconContainerStyle}
      >
        <BackrsIcon name="IconTwoArrows" alt="tier" color={appColors.neutral.black} />
      </Box>

      {/* FIRST POPOVER - Compare with... */}
      <Popover
        open={isMiddleButtonPopoverOpen}
        anchorEl={document.getElementById('popover-anchor')}
        onClose={() => setIsMiddleButtonPopoverOpen(false)}
        {...commonPopoverProps}
      >
        <Box id="secondary-popover">
          <RoundedTriangle />
          <Typography fontWeight={700}>{MARKERS_OF_SUCCESS_STRINGS.button.popoverTitle}</Typography>
          <Box
            sx={textAndArrowContainerStyle}
            onClick={() => setSecondaryPopoverContent('education')}
            onMouseEnter={() => setHoveredCompareWithElement('education')}
            onMouseLeave={() => setHoveredCompareWithElement(undefined)}
          >
            {MARKERS_OF_SUCCESS_STRINGS.button.education}
            <BackrsIcon
              name="IconArrowRight"
              alt="tier"
              color={
                hoveredCompareWithElement === 'education' || secondaryPopoverContent === 'education'
                  ? appColors.neutral.white
                  : appColors.neutral.black
              }
            />
          </Box>
          <Box
            sx={textAndArrowContainerStyle}
            onClick={() => setSecondaryPopoverContent('tracks')}
            onMouseEnter={() => setHoveredCompareWithElement('tracks')}
            onMouseLeave={() => setHoveredCompareWithElement(undefined)}
          >
            {MARKERS_OF_SUCCESS_STRINGS.button.tracks}
            <BackrsIcon
              name="IconArrowRight"
              alt="tier"
              color={
                hoveredCompareWithElement === 'tracks' || secondaryPopoverContent === 'tracks'
                  ? appColors.neutral.white
                  : appColors.neutral.black
              }
            />
          </Box>
        </Box>
      </Popover>

      {/* SECOND POPOVER - Education or Tracks */}
      <Popover
        open={!!secondaryPopoverContent}
        anchorEl={document.getElementById('secondary-popover')}
        onClose={() => setSecondaryPopoverContent(undefined)}
        {...commonPopoverProps}
      >
        {/** EDUCATION SELECTIONS */}
        {secondaryPopoverContent === 'education' && (
          <>
            {Object.values(SCHOOL_LEVEL).map((schoolLevel) => {
              if (shouldSchoolLevelBeFiltered(schoolLevel)) {
                return null;
              }

              return (
                <Box key={schoolLevel} sx={secondPopoverLineItemStyle}>
                  <Typography
                    fontWeight={tabTwoSchoolLevel === schoolLevel ? 700 : 400}
                    onClick={() => {
                      setTabTwoSchoolLevel(schoolLevel);
                      setSecondaryPopoverContent(undefined);
                      setIsMiddleButtonPopoverOpen(false);
                    }}
                    sx={secondPopoverTextStyle}
                  >
                    {schoolLevel === 'high_school'
                      ? MARKERS_OF_SUCCESS_STRINGS.highSchoolText
                      : MARKERS_OF_SUCCESS_STRINGS.collegeText}
                  </Typography>
                  {tabTwoSchoolLevel === schoolLevel && (
                    <BackrsIcon name="IconChecked" alt="check" height={16} width={16} />
                  )}
                </Box>
              );
            })}
          </>
        )}

        {/** CAREER TRACK SELECTIONS */}
        {secondaryPopoverContent === 'tracks' && (
          <Box>
            {Object.values(TIERS).map((tier) => {
              // these tiers don't have markers of success so we hide them
              if (tier === TIERS.ENTREPRENEURSHIP || tier === TIERS.SEMISKILLED) {
                return null;
              }

              return (
                <Box key={tier} sx={secondPopoverTracksLineItemStyle}>
                  <Typography
                    fontWeight={tabTwoTier === tier ? 700 : 400}
                    onClick={() => {
                      setTabTwoTier(tier);
                      setSecondaryPopoverContent(undefined);
                      setIsMiddleButtonPopoverOpen(false);
                    }}
                  >
                    {tier}
                  </Typography>
                  {tabTwoTier === tier && (
                    <BackrsIcon name="IconChecked" alt="check" height={16} width={16} />
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Popover>
    </Box>
  );
};

/** The path for this SVG was pulled from Figma */
const RoundedTriangle = () => (
  <svg
    width="22"
    height="25"
    viewBox="0 0 22 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={SVGTriangleStyle}
  >
    <path
      d="M1.75 10.3349C0.0833308 11.2972 0.0833333 13.7028 1.75 14.6651L17.5 23.7583C19.1667 24.7206 21.25 23.5178 21.25 21.5933L21.25 3.40673C21.25 1.48223 19.1667 0.279418 17.5 1.24167L1.75 10.3349Z"
      fill="black"
      stroke="black"
    />
  </svg>
);

const doubleArrowIconContainerStyle: SxProps<Theme> = {
  transform: {
    xs: 'rotate(90deg)',
    md: 'unset',
  },
} as const;

const textAndArrowContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
  mt: 1,
  fontWeight: 500,
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    opacity: 0.7,
  },
} as const;

const middleButtonStyle: SxProps<Theme> = {
  '&:hover': {
    opacity: 0.7,
  },
  cursor: 'pointer',
  backgroundColor: appColors.color.blue9,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 92,
  minWidth: 92,
  borderRadius: '100%',
  ...appBorders.backrsDropShadowBorders.black,
  borderWidth: 1,
  mx: 8,
  position: {
    xs: 'unset',
    md: 'absolute',
  },
  top: {
    md: 'calc(30%)',
    xl: 'calc(20%)',
  },
  left: 'calc(50% - 78px)',
  alignSelf: 'center',
} as const;

const paperSx: SxProps<Theme> = {
  fontSize: '12pt',
  paddingX: 3,
  paddingY: 2.5,
  backgroundColor: appColors.neutral.black,
  color: appColors.neutral.white,
  borderRadius: 3,
  whiteSpace: 'pre-wrap',
  overflow: 'visible',
  marginLeft: {
    xs: 'unset',
    sm: '36px',
  },
} as const;

const SVGTriangleStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(50% - 12px)',
  left: '-15px',
} as const;

const secondPopoverLineItemStyle: SxProps<Theme> = {
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  minWidth: '150px',
} as const;

const secondPopoverTracksLineItemStyle: SxProps<Theme> = {
  ...secondPopoverLineItemStyle,
  minWidth: '220px',
  '&:hover': {
    opacity: 0.7,
  },
} as const;

const secondPopoverTextStyle: SxProps<Theme> = {
  '&:hover': {
    opacity: 0.7,
  },
  userSelect: 'none',
  cursor: 'pointer',
} as const;
